import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAdminUsers, updateAdminUser, deleteAdminUser, register, assignSupervisor, getFellows } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { ShieldCheckIcon, TrashIcon, PencilIcon, CheckIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';

function AddFellowModal({ onClose, onAdd }) {
  const [form, setForm] = useState({ full_name: '', email: '', password: '', role: 'fellow' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-50 border border-gray-700 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h2 className="font-semibold text-white">Add Fellow</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl">&times;</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onAdd(form); }} className="p-6 space-y-4">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Full Name *</label>
            <input className="input w-full" placeholder="e.g. Chipo Nyamayaro" value={form.full_name} onChange={e => set('full_name', e.target.value)} required />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Email *</label>
            <input type="email" className="input w-full" placeholder="e.g. chipo@gmail.com" value={form.email} onChange={e => set('email', e.target.value)} required />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Temporary Password *</label>
            <input type="text" className="input w-full" placeholder="e.g. THRU2024!" value={form.password} onChange={e => set('password', e.target.value)} required minLength={6} />
            <p className="text-xs text-gray-600 mt-1">Share this with the fellow — they can change it after login.</p>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Role</label>
            <select className="input w-full" value={form.role} onChange={e => set('role', e.target.value)}>
              <option value="fellow">Fellow</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Admin</option>
              <option value="guest">Guest</option>
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" className="btn-primary flex-1">Add Fellow</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ROLE_COLORS = {
  admin: 'bg-red-900/50 text-red-300',
  fellow: 'bg-blue-900/50 text-blue-300',
  supervisor: 'bg-purple-900/50 text-purple-300',
  guest: 'bg-gray-800 text-gray-400',
};

export default function AdminPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ['admin-users'], queryFn: getAdminUsers });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => updateAdminUser(id, data),
    onSuccess: () => { qc.invalidateQueries(['admin-users']); toast.success('User updated'); setEditId(null); },
    onError: () => toast.error('Update failed'),
  });

  const deleteMut = useMutation({
    mutationFn: deleteAdminUser,
    onSuccess: () => { qc.invalidateQueries(['admin-users']); toast.success('User removed'); },
    onError: () => toast.error('Delete failed'),
  });

  const addMut = useMutation({
    mutationFn: register,
    onSuccess: () => { qc.invalidateQueries(['admin-users']); toast.success('Fellow added!'); setShowAdd(false); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Email already exists'),
  });

  const users = data?.data || [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <ShieldCheckIcon className="w-5 h-5 text-primary-400" /> Admin Panel
          </h1>
          <p className="text-gray-500 text-sm">{users.length} registered users</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-2">
          <PlusIcon className="w-4 h-4" /> Add Fellow
        </button>
      </div>

      {isLoading ? (
        <div className="text-gray-400 text-sm">Loading users...</div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-left">
                <th className="pb-3 text-gray-500 font-medium">Name</th>
                <th className="pb-3 text-gray-500 font-medium">Email</th>
                <th className="pb-3 text-gray-500 font-medium">Role</th>
                <th className="pb-3 text-gray-500 font-medium">Status</th>
                <th className="pb-3 text-gray-500 font-medium">Joined</th>
                <th className="pb-3 text-gray-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-surface-100 transition-all">
                  <td className="py-3 font-medium text-white">
                    {editId === u.id ? (
                      <input className="input w-40 text-xs py-1" value={editForm.full_name || ''} onChange={(e) => setEditForm(f => ({ ...f, full_name: e.target.value }))} />
                    ) : (
                      <span className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-primary-600/30 flex items-center justify-center text-primary-400 text-xs font-bold flex-shrink-0">
                          {u.full_name?.[0]?.toUpperCase()}
                        </div>
                        {u.full_name}
                      </span>
                    )}
                  </td>
                  <td className="py-3 text-gray-400">{u.email}</td>
                  <td className="py-3">
                    {editId === u.id ? (
                      <select className="input w-32 text-xs py-1" value={editForm.role || u.role} onChange={(e) => setEditForm(f => ({ ...f, role: e.target.value }))}>
                        <option value="admin">Admin</option>
                        <option value="fellow">Fellow</option>
                        <option value="supervisor">Supervisor</option>
                        <option value="guest">Guest</option>
                      </select>
                    ) : (
                      <span className={`badge ${ROLE_COLORS[u.role]}`}>{u.role}</span>
                    )}
                  </td>
                  <td className="py-3">
                    {editId === u.id ? (
                      <label className="flex items-center gap-1 text-xs text-gray-400 cursor-pointer">
                        <input type="checkbox" checked={editForm.is_active ?? u.is_active} onChange={(e) => setEditForm(f => ({ ...f, is_active: e.target.checked }))} />
                        Active
                      </label>
                    ) : (
                      <span className={`badge ${u.is_active ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'}`}>
                        {u.is_active ? 'Active' : 'Disabled'}
                      </span>
                    )}
                  </td>
                  <td className="py-3 text-gray-500 text-xs">{format(new Date(u.created_at), 'dd MMM yyyy')}</td>
                  <td className="py-3">
                    {editId === u.id ? (
                      <div className="flex gap-2">
                        <button onClick={() => updateMut.mutate({ id: u.id, data: editForm })} className="text-green-400 hover:text-green-300">
                          <CheckIcon className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditId(null)} className="text-gray-400 hover:text-white">
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={() => { setEditId(u.id); setEditForm({ full_name: u.full_name, role: u.role, is_active: u.is_active }); }} className="text-gray-400 hover:text-white">
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        {u.id !== user?.id && (
                          <button onClick={() => { if (window.confirm(`Remove ${u.full_name}?`)) deleteMut.mutate(u.id); }} className="text-red-400 hover:text-red-300">
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Supervisor Assignment */}
      <SupervisorAssignment qc={qc} allUsers={users} />

      {showAdd && (
        <AddFellowModal
          onClose={() => setShowAdd(false)}
          onAdd={(data) => addMut.mutate(data)}
        />
      )}
    </div>
  );
}

function SupervisorAssignment({ qc, allUsers }) {
  const supervisors = allUsers.filter(u => u.role === 'supervisor');
  const { data: fellowsData } = useQuery({ queryKey: ['fellows-full'], queryFn: () => getFellows() });
  const fellows = fellowsData?.data || [];

  const assignMut = useMutation({
    mutationFn: assignSupervisor,
    onSuccess: () => { qc.invalidateQueries(['fellows-full']); toast.success('Supervisor assigned'); },
    onError: () => toast.error('Failed to assign'),
  });

  if (supervisors.length === 0) {
    return (
      <div className="card">
        <h2 className="font-semibold text-white mb-2">Supervisor Assignments</h2>
        <p className="text-gray-500 text-sm">
          No supervisors yet. Add a user and set their role to <span className="text-primary-400">supervisor</span> to enable this section.
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="font-semibold text-white mb-1">Supervisor Assignments</h2>
      <p className="text-gray-500 text-sm mb-4">Assign a supervisor to each fellow so they can view their fellows' progress when they log in.</p>
      <div className="space-y-2">
        {fellows.map((f) => (
          <div key={f.id} className="flex items-center justify-between gap-4 py-2 border-b border-gray-800 last:border-0">
            <div className="text-sm text-gray-200 font-medium">{f.user?.full_name}</div>
            <select
              className="input text-xs py-1 w-48"
              value={f.supervisor_user_id || ''}
              onChange={(e) => assignMut.mutate({ fellow_id: f.user_id, supervisor_id: e.target.value ? parseInt(e.target.value) : null })}
            >
              <option value="">— No supervisor —</option>
              {supervisors.map(s => (
                <option key={s.id} value={s.id}>{s.full_name}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}
